/**
 * Amazon Ads — DSP Funnel-Event Column Map (Story 19.16.1, Task 6)
 *
 * 145 metrics = 29 funnel-event names × 5 suffix-fields. Generated
 * programmatically via nested for-each — hand-rolling 145 entries is too
 * brittle (per-entry typo risk × 145 = high probability of at least one
 * wrong column name shipping).
 *
 * Per Round 3 Vuln 3 — the `DSP_FUNNEL_EVENT_NAMES` tuple is `as const`,
 * compile-time-frozen, NEVER hot-loaded from KV/D1. This prevents injection
 * vectors against the generation loop.
 *
 * Per AC #5 — every generated entry passes the same validation as hand-rolled
 * metrics (key === columnName, attributionWindowDays = 14, dataType from
 * suffix, compatibleWidgets per category). A frozen golden-snapshot lock test
 * (`Object.keys(DSP_FUNNEL_COLUMN_MAP).length === 145`) catches off-by-one
 * errors in either dimension.
 */

import type { DspMetricColumnEntry, DspMetricDataType } from "./dsp-column-map";

const KPI_LINE_BAR_TABLE = ["kpi", "line-chart", "bar-chart", "data-table"] as const;
const KPI_LINE_TABLE = ["kpi", "line-chart", "data-table"] as const;
const KPI_GAUGE_LINE = ["kpi", "gauge", "line-chart"] as const;

/**
 * Story 19.16.1 AC #5 — frozen 29-event tuple. ADD-ONLY policy: appending an
 * event name extends the catalogue; reordering or removing breaks the lock
 * test in CI. NEVER hot-load this from KV / D1 (Round 3 Vuln 3 defense).
 */
export const DSP_FUNNEL_EVENT_NAMES = [
  "Accept",
  "Decline",
  "Application",
  "BannerInteraction",
  "Engagement",
  "Submit",
  "Quote",
  "Lead",
  "SignUp",
  "Donation",
  "RegistrationStart",
  "RegistrationComplete",
  "Subscription",
  "TrialStart",
  "Reservation",
  "BookingComplete",
  "BrochureRequest",
  "Inquiry",
  "Search",
  "LocateStore",
  "WatchVideo",
  "ListenAudio",
  "ShareSocial",
  "Bookmark",
  "Print",
  "Download",
  "OutboundClick",
  "Login",
  "ProfileUpdate",
] as const;

/**
 * Story 19.16.1 AC #5 — 5-field suffix family per funnel event.
 * dataType mapping (Task 6.4):
 *   - `14d` → Integer
 *   - `Clicks14d` → Integer
 *   - `Views14d` → Integer
 *   - `CPA14d` → Currency
 *   - `CVR14d` → Percentage
 */
export const DSP_FUNNEL_EVENT_SUFFIXES = [
  "14d",
  "Clicks14d",
  "Views14d",
  "CPA14d",
  "CVR14d",
] as const;

export type DspFunnelEventName = (typeof DSP_FUNNEL_EVENT_NAMES)[number];
export type DspFunnelEventSuffix = (typeof DSP_FUNNEL_EVENT_SUFFIXES)[number];

function dataTypeFromSuffix(suffix: DspFunnelEventSuffix): DspMetricDataType {
  if (suffix === "CPA14d") return "Currency";
  if (suffix === "CVR14d") return "Percentage";
  return "Integer";
}

function widgetsFromSuffix(suffix: DspFunnelEventSuffix): readonly string[] {
  if (suffix === "CPA14d") return KPI_LINE_TABLE;
  if (suffix === "CVR14d") return KPI_GAUGE_LINE;
  return KPI_LINE_BAR_TABLE;
}

function labelFor(event: DspFunnelEventName, suffix: DspFunnelEventSuffix): string {
  return `${event} (${suffix})`;
}

/**
 * Generate the 145-entry funnel-event column map. Done eagerly at module-load
 * time so the resulting object is referentially stable across imports.
 */
function generateFunnelColumnMap(): Record<string, DspMetricColumnEntry> {
  const out: Record<string, DspMetricColumnEntry> = {};
  for (const event of DSP_FUNNEL_EVENT_NAMES) {
    for (const suffix of DSP_FUNNEL_EVENT_SUFFIXES) {
      const key = `${event}${suffix}`;
      out[key] = {
        key,
        label: labelFor(event, suffix),
        // Funnel events live within DSP_CAMPAIGN — the standard programmatic
        // reporting surface. Per Amazon Postman collection.
        reportTypeId: "DSP_CAMPAIGN",
        columnName: key,
        dataType: dataTypeFromSuffix(suffix),
        attributionWindowDays: 14,
        // Story 31-31 — annotated AT THE GENERATOR (AC #4): the three count
        // suffixes (14d / Clicks14d / Views14d) are additive event counts.
        // CPA14d = totalCost ÷ {event}14d → weighted by the event count.
        // CVR14d is the event's conversion rate; Amazon's DSP rates are
        // expressed relative to impressions, so the platform-wide DSP
        // `impressions` metric is the weight.
        ...(suffix === "CPA14d"
          ? {
              aggregationKind: "ratio" as const,
              ratioComponents: { numerator: "totalCost", denominator: `${event}14d` },
            }
          : suffix === "CVR14d"
            ? {
                aggregationKind: "ratio" as const,
                ratioComponents: { numerator: `${event}14d`, denominator: "impressions" },
              }
            : { aggregationKind: "additive" as const }),
        compatibleWidgets: [...widgetsFromSuffix(suffix)],
      };
    }
  }
  return out;
}

export const DSP_FUNNEL_COLUMN_MAP: Record<string, DspMetricColumnEntry> =
  generateFunnelColumnMap();

/** Story 19.16.1 AC #5 — frozen golden-snapshot lock count. */
export const DSP_FUNNEL_COLUMN_COUNT = 145;

// ============================================================================
// Story 35.7 AC #6 + AC #9 — `amazon_ads_dsp_funnel_event_audit` module-load
// ============================================================================

/**
 * Compute a stable forensic hash of the frozen event-name tuple. Used by the
 * `amazon_ads_dsp_funnel_event_audit` log to make accidental tuple drift
 * forensically detectable in Grafana even when the column count remains 145
 * (e.g. if an event is renamed at the same time another is added). Uses a
 * simple FNV-1a 32-bit string hash for portability (Workers runtime has no
 * crypto.subtle in synchronous module-load context).
 *
 * Implementation note: `Math.imul` is REQUIRED for correctness. JS native `*`
 * uses double-precision floats whose 53-bit mantissa cannot represent the
 * full uint32 product space (`0xFFFFFFFF * 0x01000193 ≈ 7.2e16` overflows
 * mantissa precision); the result rounds before the `>>> 0` cast, dropping
 * low bits and producing a collision-prone hash. `Math.imul` performs a true
 * int32 multiply (per ECMAScript spec) that round-trips through Bun / Deno /
 * V8 / WorkerD identically (Code Review Round 1 — forensic continuity).
 */
function fnv1aHash(input: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, "0");
}

/**
 * Story 35.7 AC #6 + AC #9 — emits with the funnel tuple shape invariant +
 * a stable hash of the event-name tuple. Allows Grafana alerting on shape
 * regression OR a silent tuple-rename. Per Round 1 Privacy Officer +
 * 19.13 AC #11 — log carries ZERO tenant/token/customer data (only
 * frozen-compile-time constants).
 *
 * **Invocation contract (Code Review Round 1 — explicit-call pattern):**
 * Callers (the adapter's `validateConnection` first-call path + tests)
 * invoke this directly. The prior module-load-time auto-emit pattern
 * pollutes test stdout (Vitest workers re-instantiate the module per file)
 * AND fires on every Workers isolate cold start in production
 * (uncontrolled Grafana volume). Explicit invocation gives callers control
 * over emission frequency.
 */
export function emitDspFunnelEventAuditLog(): void {
  const tupleHash = fnv1aHash(DSP_FUNNEL_EVENT_NAMES.join(","));
  // Round 2 — derive `expectedColumns` and `generationRule` from the runtime
  // tuples so the audit log STAYS HONEST if a future PR edits the event-name
  // tuple. Sourcing from the frozen `DSP_FUNNEL_COLUMN_COUNT` constant (145)
  // would let the log silently mis-report after a tuple change. The frozen
  // constant is still asserted by the dedicated test as a separate
  // invariant — both pieces of redundancy are intentional.
  const eventCount = DSP_FUNNEL_EVENT_NAMES.length;
  const suffixCount = DSP_FUNNEL_EVENT_SUFFIXES.length;
  const expectedColumns = eventCount * suffixCount;
  const event = {
    structured_log: "amazon_ads_dsp_funnel_event_audit",
    storyId: "35.7",
    tupleHash,
    eventCount,
    suffixCount,
    expectedColumns,
    generationRule: `${eventCount} events × ${suffixCount} suffixes`,
  };
  console.log(JSON.stringify(event));
}
